import {
  LayoutDashboard, FileSpreadsheet, UserCircle, Landmark, BookOpen, Layers,
  SlidersHorizontal, ShoppingCart, PackageSearch, Truck, Users2, FolderKanban,
  Factory, Warehouse, Building, Wrench, UsersRound, ShieldCheck, BarChart3,
  Scale, Settings2, LineChart, GitFork, DraftingCompass, Boxes, HardHat,
  ClipboardList, BellRing, AlertTriangle, Users, CalendarDays, History, Settings,
  Compass, LayoutGrid, Calculator, Receipt, Clock, FileText, Gauge,
  Percent, Gavel, ScrollText, KeyRound, HeartPulse, TrendingUp,
} from "lucide-react";

export function slugify(text) {
  return text
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

// Fixed 11 pages that already exist per-project (Part 1/2 of the DPR platform).
// Only rendered when a :projectId is present in the current route.
export const PROJECT_DPR_PAGES = [
  { suffix: "", label: "Dashboard", icon: LineChart, testId: "sidebar-link-dashboard", end: true },
  { suffix: "wbs", label: "WBS & Weightages", icon: GitFork, testId: "sidebar-link-wbs" },
  { suffix: "engineering", label: "Engineering DPR", icon: DraftingCompass, testId: "sidebar-link-engineering" },
  { suffix: "materials", label: "Materials DPR", icon: Boxes, testId: "sidebar-link-materials" },
  { suffix: "construction", label: "Construction DPR", icon: HardHat, testId: "sidebar-link-construction" },
  { suffix: "plan-vs-actual", label: "Plan vs Actual", icon: ClipboardList, testId: "sidebar-link-plan-vs-actual" },
  { suffix: "alerts", label: "Alerts & Overdue", icon: BellRing, testId: "sidebar-link-alerts" },
  { suffix: "issues", label: "Issues & Risks", icon: AlertTriangle, testId: "sidebar-link-issues" },
  { suffix: "resources", label: "Resources & Manpower", icon: Users, testId: "sidebar-link-resources" },
  { suffix: "schedule", label: "Master Schedule", icon: CalendarDays, testId: "sidebar-link-schedule" },
  { suffix: "360", label: "Project 360", icon: Compass, testId: "sidebar-link-project-360" },
  { suffix: "finance", label: "Project Finance", icon: Landmark, testId: "sidebar-link-project-finance" },
  { suffix: "site-hub", label: "Site Management Hub", icon: LayoutGrid, testId: "sidebar-link-site-hub" },
  { suffix: "estimates", label: "Estimates & Budgets", icon: Calculator, testId: "sidebar-link-estimates" },
  { suffix: "cost-entries", label: "Cost Entries", icon: Receipt, testId: "sidebar-link-cost-entries" },
  { suffix: "delays", label: "Delays", icon: Clock, testId: "sidebar-link-delays" },
  { suffix: "daily-reports", label: "Daily Progress Reports", icon: FileText, testId: "sidebar-link-daily-reports" },
  { suffix: "resource-planning", label: "Resource Planning", icon: Gauge, testId: "sidebar-link-resource-planning" },
  { suffix: "logbook", label: "Equipment Logbook", icon: Wrench, testId: "sidebar-link-logbook" },
  { suffix: "audit", label: "Audit Trail", icon: History, testId: "sidebar-link-audit" },
  { suffix: "settings", label: "Project Settings", icon: Settings, testId: "sidebar-link-settings" },
];

// Raw nav tree. Leaves without an explicit `to` route to the shared ComingSoon
// page at /erp/:group/:module (auto-generated below). `restricted` hides a
// group/item from users who are not ORG_ADMIN / PORTFOLIO_LEAD.
const RAW_GROUPS = [
  {
    id: "global", label: "Global", icon: LayoutDashboard,
    items: [
      { id: "portfolio", label: "Portfolio Overview", icon: LayoutDashboard, to: "/", end: true, testId: "sidebar-link-portfolio" },
      { id: "import", label: "Workbook Import", icon: FileSpreadsheet, to: "/import", testId: "sidebar-link-import", orgAdminOnly: true },
      { id: "workspace", label: "My workspace", icon: UserCircle },
    ],
  },
  {
    id: "finance-and-accounting", label: "Finance & Accounting", icon: Landmark, restricted: true,
    items: [
      {
        id: "general-ledger", label: "General Ledger", icon: BookOpen,
        children: [
          { id: "chart-of-accounts", label: "Chart of Accounts" },
          { id: "post-journal", label: "Post Journal" },
          { id: "periods-year-end", label: "Periods & Year-End" },
          { id: "ledger-tally-export", label: "Ledger & Tally export" },
          { id: "inter-company-loans", label: "Inter-company loans" },
        ],
      },
    ],
  },
  {
    id: "operations", label: "Operations", icon: Layers,
    items: [
      {
        id: "masters", label: "Masters", icon: SlidersHorizontal,
        children: [
          { id: "companies", label: "Companies" },
          { id: "branches", label: "Branches" },
          { id: "units-of-measure", label: "Units of Measure" },
          { id: "tax-codes", label: "Tax Codes" },
          { id: "numbering-series", label: "Numbering Series" },
          { id: "approval-workflows", label: "Approval Workflows" },
        ],
      },
      {
        id: "procurement", label: "Procurement", icon: ShoppingCart,
        children: [
          { id: "vendors", label: "Vendors" },
          { id: "purchase-indents", label: "Purchase Indents" },
          { id: "purchase-requisitions", label: "Purchase Requisitions" },
          { id: "rfqs", label: "RFQs" },
          { id: "purchase-orders", label: "Purchase Orders" },
          { id: "goods-receipt-notes", label: "Goods Receipt Notes" },
          { id: "pipeline", label: "Pipeline" },
          { id: "service-requisitions", label: "Service Requisitions" },
          { id: "work-orders", label: "Work Orders" },
          { id: "service-bills", label: "Service Bills" },
          { id: "purchase-bills", label: "Purchase Bills" },
          { id: "contractor-jmrs", label: "Contractor JMRs" },
          { id: "vendor-advances", label: "Vendor Advances" },
          { id: "vendor-payments", label: "Vendor Payments" },
          { id: "vendor-ledger", label: "Vendor Ledger" },
          { id: "debit-notes", label: "Debit Notes" },
          { id: "procurement-tracker", label: "Procurement Tracker" },
        ],
      },
      {
        id: "inventory", label: "Inventory", icon: PackageSearch,
        children: [
          { id: "items", label: "Items" },
          { id: "warehouses", label: "Warehouses" },
          { id: "stock-and-balances", label: "Stock & Balances" },
          { id: "receipts", label: "Receipts" },
          { id: "goods-issues", label: "Goods Issues" },
          { id: "store-returns", label: "Store Returns" },
          { id: "adjustments", label: "Adjustments" },
          { id: "transfers", label: "Transfers" },
        ],
      },
    ],
  },
  {
    id: "delivery", label: "Delivery", icon: Truck,
    items: [
      {
        id: "client-management", label: "Client Management", icon: Users2,
        children: [
          { id: "clients", label: "Clients" },
          { id: "leads", label: "Leads" },
          { id: "tenders", label: "Tenders" },
          { id: "proposals", label: "Proposals" },
          { id: "contracts", label: "Contracts" },
          { id: "joint-measurement", label: "Joint Measurement" },
          { id: "ra-bills", label: "RA Bills" },
          { id: "client-payments", label: "Client Payments" },
          { id: "client-ledger", label: "Client Ledger" },
          { id: "certify-ra-bills", label: "Certify RA Bills" },
          { id: "client-advances", label: "Client Advances" },
          { id: "site-measurements", label: "Site Measurements" },
        ],
      },
      {
        id: "projects", label: "Projects", icon: FolderKanban,
        children: [
          { id: "all-projects", label: "All projects" },
          { id: "setup-library", label: "Setup Library" },
          { id: "project-progress-dpr", label: "Project progress (DPR)", isProjectSection: true },
        ],
      },
      { id: "production", label: "Production", icon: Factory, children: [
        { id: "plants-and-bom", label: "Plants & BOM" },
        { id: "production-runs", label: "Production Runs" },
        { id: "dispatches", label: "Dispatches" },
      ] },
    ],
  },
  {
    id: "assets-and-plant", label: "Assets & Plant", icon: Warehouse,
    items: [
      { id: "fixed-assets", label: "Fixed Assets", icon: Building, children: [
        { id: "asset-register", label: "Asset Register" },
        { id: "depreciation-runs", label: "Depreciation Runs" },
      ] },
      { id: "plant-and-machinery", label: "Plant & Machinery", icon: Wrench, children: [
        { id: "equipment", label: "Equipment" },
        { id: "hire-orders", label: "Hire Orders" },
        { id: "weighbridge", label: "Weighbridge" },
      ] },
    ],
  },
  {
    id: "people-and-insight", label: "People & Insight", icon: UsersRound,
    items: [
      { id: "hr-and-payroll", label: "HR & Payroll", icon: Users },
      { id: "statutory", label: "Statutory", icon: ShieldCheck, restricted: true },
      { id: "reports-and-mis", label: "Reports & MIS", icon: BarChart3, children: [
        { id: "executive-dashboard", label: "Executive Dashboard", icon: Gauge },
        { id: "financial-statements", label: "Financial Statements", icon: FileSpreadsheet },
        { id: "aging-reports", label: "Aging Reports", icon: History },
        { id: "cost-to-completion", label: "Cost to Completion", icon: TrendingUp },
        { id: "operations-reports", label: "Operations Reports", icon: Percent },
      ] },
    ],
  },
  {
    id: "governance", label: "Governance", icon: Scale,
    items: [
      { id: "legal-and-liaison", label: "Legal & Liaison", icon: Scale, children: [
        { id: "agreements", label: "Agreements", icon: ScrollText },
        { id: "statutory-approvals", label: "Statutory Approvals", icon: KeyRound },
        { id: "cases-and-notices", label: "Cases & Notices", icon: Gavel },
      ] },
      { id: "administration", label: "Administration", icon: Settings2, restricted: true, children: [
        { id: "users", label: "Users", icon: Users2 },
        { id: "permission-matrix", label: "Permission Matrix", icon: KeyRound },
        { id: "audit-log", label: "Audit Log", icon: History },
        { id: "org-settings", label: "Organization Settings", icon: Settings },
        { id: "system-health", label: "Backup & Health", icon: HeartPulse },
        { id: "integrity-checks", label: "Integrity Checks", icon: ShieldCheck },
      ] },
    ],
  },
];

function enrich(node, groupSlug, path, routeLabels, groupLabel) {
  const id = [...path, node.id].join(".");
  const slug = slugify(node.label);
  if (node.isProjectSection) {
    return { ...node, id, slug };
  }
  if (node.children) {
    const children = node.children.map((c) => enrich(c, groupSlug, [...path, node.id], routeLabels, groupLabel));
    return { ...node, id, slug, children };
  }
  let to = node.to;
  let testId = node.testId;
  if (!to) {
    to = `/erp/${groupSlug}/${slug}`;
    routeLabels[`${groupSlug}/${slug}`] = { label: node.label, groupLabel };
  }
  if (!testId) testId = `sidebar-link-${slug}`;
  return { ...node, id, slug, to, testId };
}

export const ROUTE_LABELS = {};

export const NAV_GROUPS = RAW_GROUPS.map((group) => {
  const groupSlug = slugify(group.label);
  const items = group.items.map((item) => enrich(item, groupSlug, [group.id], ROUTE_LABELS, group.label));
  return { ...group, slug: groupSlug, items };
});

function filterItemsByRole(items, ctx) {
  return items
    .filter((i) => (!i.restricted || ctx.seesWholePortfolio) && (!i.orgAdminOnly || ctx.isOrgAdmin))
    .map((i) => (i.children ? { ...i, children: filterItemsByRole(i.children, ctx) } : i))
    .filter((i) => i.isProjectSection || !i.children || i.children.length > 0);
}

export function applyRoleFilter(groups, ctx) {
  return groups
    .filter((g) => !g.restricted || ctx.seesWholePortfolio)
    .map((g) => ({ ...g, items: filterItemsByRole(g.items, ctx) }))
    .filter((g) => g.items.length > 0);
}

function matches(label, q) {
  return label.toLowerCase().includes(q);
}

function filterNode(node, q, projectId) {
  if (node.isProjectSection) {
    if (!projectId) return null;
    const dynamicChildren = PROJECT_DPR_PAGES.filter((p) => matches(p.label, q));
    if (dynamicChildren.length === 0 && !matches(node.label, q)) return null;
    return { ...node, dynamicChildren: dynamicChildren.length ? dynamicChildren : PROJECT_DPR_PAGES };
  }
  if (node.children) {
    const children = node.children.map((c) => filterNode(c, q, projectId)).filter(Boolean);
    if (children.length === 0 && !matches(node.label, q)) return null;
    return { ...node, children: children.length ? children : node.children };
  }
  return matches(node.label, q) ? node : null;
}

export function filterGroups(groups, q, projectId) {
  if (!q) return groups;
  return groups
    .map((g) => {
      const items = g.items.map((i) => filterNode(i, q, projectId)).filter(Boolean);
      if (items.length === 0 && !matches(g.label, q)) return null;
      return { ...g, items: items.length ? items : g.items };
    })
    .filter(Boolean);
}

export function flattenLeaves(groups, projectId) {
  const out = [];
  function walk(node) {
    if (node.isProjectSection) {
      (node.dynamicChildren || (projectId ? PROJECT_DPR_PAGES : [])).forEach((p) => {
        out.push({ to: `/projects/${projectId}/${p.suffix}`, label: p.label });
      });
      return;
    }
    if (node.children) {
      node.children.forEach(walk);
      return;
    }
    out.push({ to: node.to, label: node.label });
  }
  groups.forEach((g) => g.items.forEach(walk));
  return out;
}

export function findActivePath(groups, pathname, projectId) {
  function isLeafActive(to) {
    if (to === "/") return pathname === "/";
    return pathname === to || pathname.startsWith(`${to}/`);
  }
  function search(node, ancestors) {
    if (node.isProjectSection) {
      if (!projectId) return null;
      const active = PROJECT_DPR_PAGES.some((p) => isLeafActive(`/projects/${projectId}/${p.suffix}`.replace(/\/$/, "")));
      return active ? [...ancestors, node.id] : null;
    }
    if (node.children) {
      for (const child of node.children) {
        const found = search(child, [...ancestors, node.id]);
        if (found) return found;
      }
      return null;
    }
    return isLeafActive(node.to) ? ancestors : null;
  }
  for (const group of groups) {
    for (const item of group.items) {
      const found = search(item, []);
      if (found) return found;
    }
  }
  return [];
}

export function isGroupActive(group, pathname, projectId) {
  function isLeafActive(to) {
    if (to === "/") return pathname === "/";
    return pathname === to || pathname.startsWith(`${to}/`);
  }
  function check(node) {
    if (node.isProjectSection) {
      if (!projectId) return false;
      return PROJECT_DPR_PAGES.some((p) => isLeafActive(`/projects/${projectId}/${p.suffix}`.replace(/\/$/, "")));
    }
    if (node.children) return node.children.some(check);
    return isLeafActive(node.to);
  }
  return group.items.some(check);
}
